import { createContext, useContext, type ReactNode } from 'react'

/**
 * Whether the address postcode auto-fill is enabled for the active tenant.
 *
 * The `AddressInput` / `AddressCapture` fields are shared across the admin and
 * consumer apps, but each reads flags from a different source. Each app provides
 * this context from its own: admin from the raw flag map, consumer from the
 * `feature_flags` on the consumer-branding payload - so the shared field can gate
 * the behaviour without knowing which surface it renders on. Auto-fill is on
 * platform-wide; a tenant opts out with an explicit `postcode_autofill: false`,
 * which each provider resolves before feeding this context. The bare-context
 * default is `false` (fail-safe: no autofill when no provider is mounted); the
 * two app roots always mount a provider, so real surfaces get the resolved value.
 */
const AddressAutofillContext = createContext(false)

export function AddressAutofillProvider({
  enabled,
  children,
}: {
  enabled: boolean
  children: ReactNode
}) {
  return (
    <AddressAutofillContext.Provider value={enabled}>
      {children}
    </AddressAutofillContext.Provider>
  )
}

/** Read whether address postcode auto-fill is enabled for the active tenant. */
// eslint-disable-next-line react-refresh/only-export-components
export function useAddressAutofill(): boolean {
  return useContext(AddressAutofillContext)
}
